export type EmissionRegisterSuccessAction = 'new-emission' | 'consult' | 'dismiss';

export type EmissionRegisterErrorAction = 'retry' | 'support' | 'dismiss';

export interface EmissionRegisterSuccessViewModel {
  title: string;
  description: string;
  projectName: string;
  sentDateLabel: string;
  statusLabel: string;
}

export interface EmissionRegisterErrorViewModel {
  title: string;
  description: string;
}

export interface EmissionRegisterApiResponse {
  data?: {
    project?: {
      name?: string;
    };
    projectName?: string;
    insertDate?: string;
    status?: string;
  };
}

const EMISSION_STATUS_LABELS: Readonly<Record<string, string>> = {
  PENDING_AUDITOR_VERIFICATION: 'Pendente de verificação do auditor',
  APPROVED_BY_AUDITOR: 'Aprovado pelo auditor',
  REJECTED_BY_AUDITOR: 'Rejeitado pelo auditor',
  ADJUSTED_BY_DEVELOPER: 'Ajustado pelo desenvolvedor',
  PENDING_DEVELOPER_CONFIRMATION: 'Pendente de confirmação do desenvolvedor',
  CONFIRMED_BY_DEVELOPER: 'Confirmado pelo desenvolvedor',
  REJECTED_BY_DEVELOPER: 'Rejeitado pelo desenvolvedor',
  REGISTERED_IN_B3: 'Registrado na B3',
};

function formatSentDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export function formatEmissionSentDateFromApi(value?: string | null): string {
  if (!value) {
    return formatSentDate(new Date());
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return formatSentDate(parsed);
}

export function resolveEmissionStatusLabel(status?: string | null): string {
  if (!status) {
    return EMISSION_STATUS_LABELS['PENDING_AUDITOR_VERIFICATION'];
  }

  return EMISSION_STATUS_LABELS[status] ?? status;
}

export function resolveEmissionProjectName(
  response: EmissionRegisterApiResponse | null | undefined,
  fallbackProjectName = '',
): string {
  const data = response?.data;
  const fromProject = data?.project?.name?.trim();
  if (fromProject) {
    return fromProject;
  }

  const fromRoot = data?.projectName?.trim();
  if (fromRoot) {
    return fromRoot;
  }

  return fallbackProjectName || '-';
}

export function buildEmissionRegisterSuccessViewModel(
  response: EmissionRegisterApiResponse | unknown,
  fallbackProjectName = '',
): EmissionRegisterSuccessViewModel {
  const payload = (response ?? {}) as EmissionRegisterApiResponse;

  return {
    title: 'Emissão enviada para verificação.',
    description: 'A emissão foi enviada e está em processo de verificação pelo auditor.',
    projectName: resolveEmissionProjectName(payload, fallbackProjectName),
    sentDateLabel: formatEmissionSentDateFromApi(payload.data?.insertDate),
    statusLabel: resolveEmissionStatusLabel(payload.data?.status),
  };
}

export function buildEmissionRegisterErrorViewModel(): EmissionRegisterErrorViewModel {
  return {
    title: 'Erro!',
    description:
      'Não foi possível concluir a emissão dos créditos. Verifique os dados e tente novamente ou entre em contato com o suporte.',
  };
}
